/**
 * `useClipboardHandlers` — paste, copy, drag-over, drop handlers + plain-text
 * paste intent. Extracted from PlateEditor.tsx so the main shell stays focused
 * on render orchestration rather than clipboard policy.
 *
 * Returns the four event handlers ready to attach to `<PlateContent>` /
 * `<Plate>`, plus `markNextPasteAsPlain()` — call this from the editor's
 * keydown handler when ⌘⇧V / Ctrl⇧V is detected. The paste event fires
 * after keydown, so we record intent here and consume it in `handlePaste`.
 *
 * What each handler does:
 *
 *  handlePaste:
 *    - ⌘⇧V intent: insert clipboard's plain text only, splitting on
 *      newlines into multiple paragraphs. Ignores HTML AND Slate's
 *      `application/x-slate-fragment` back-channel (which would otherwise
 *      restore every mark from the source editor). Mirrors Google Docs /
 *      Notion behavior.
 *    - Image file in clipboard: routes through `handleImageInsert` (the
 *      shared upload+insert pipeline). Toast on invalid mime / too-large.
 *    - Genuine spreadsheet HTML (Excel / Google Sheets, per
 *      `isSpreadsheetTableHtml`): routes through `spreadsheetHtmlToPlateTable`
 *      for fidelity. Detection MUST run before `sanitizeOfficeHtml` strips the
 *      `<style>` blocks Excel uses as its marker. Document tables (Google Docs,
 *      generic HTML) deliberately fall through to the generic path below, which
 *      preserves in-cell lists the converter would flatten.
 *    - Everything else (the common case, incl. document tables): runs through
 *      `deserializePastedHtml` (sanitise → strip `data-slate-*` → flatten/enrich
 *      lists → deserialize → layout-tables → strip newlines) and inserts the
 *      resulting fragment. Plain-text fallback if it deserializes to nothing.
 *
 *  handleDrop:
 *    - File drop of any image MIME → routes through `handleImageInsert`.
 *    - preventDefault always fires to dodge Slate's "Cannot resolve a Slate
 *      range" error on file drops.
 *
 *  handleDragOver:
 *    - Calls preventDefault when the drag payload includes Files, so the
 *      browser will dispatch the drop event to us instead of refusing it.
 *
 *  handleCopy:
 *    - Clones the current selection, runs `cleanDomForExport` (strips
 *      editor-only UI + normalises tables / images / data-slate attrs),
 *      and writes the cleaned HTML to `text/html` + plain text to
 *      `text/plain`. So pasting from this editor into Google Docs / Word
 *      / a plain-text input all produce the right shape.
 */
import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { isSpreadsheetTableHtml, isPureTableHtml, spreadsheetHtmlToPlateTable } from '../serializers/spreadsheetHtmlToPlate';
import {
    deserializePastedHtml,
    stripNewlinesFromFragment,
    enrichPastedListElements,
    reattachTableColSizes,
} from '../serializers/pasteNormalize';
import { simplifyPastedFragment } from '../serializers/simplifyPastedFragment';
import { handleImageInsert, rehostRemoteImagesAfterPaste } from '../components/ImagePlugin/handleImageInsert';
import { cleanDomForExport } from '../exporters/docxExport';
import { scrollCaretIntoView } from './scrollCaretIntoView';

export interface UseClipboardHandlersConfig {
    editor: any;
    readOnly: boolean;
    /**
     * `uploadSource` tag passed through to `handleImageInsert` so the upload
     * pipeline knows which surface initiated the upload (one-pager, email,
     * comment, etc.) for analytics + scoping.
     */
    uploadSource: string;
    /**
     * Strip foreign text styling (colour / font size / family / background) and
     * flatten layout tables on paste, keeping links + lists + basic marks. Used
     * by compact inputs like the comments box so pasted rich HTML (e.g. a Fathom
     * email) lands as clean text rather than washed-out, oversized, table-wrapped
     * content (RECUR-26761). Default off — document editors keep full fidelity.
     */
    simplifyPaste?: boolean;
}

export interface UseClipboardHandlersReturn {
    handlePaste: (e: React.ClipboardEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleCopy: (e: React.ClipboardEvent) => void;
    /**
     * Call this from the editor's keydown handler when ⌘⇧V / Ctrl⇧V is
     * detected. The paste event fires immediately after, and the next
     * `handlePaste` invocation reads this flag and switches to plain-text
     * mode. The flag is one-shot — consumed by the next paste regardless
     * of mode.
     */
    markNextPasteAsPlain: () => void;
}

export function useClipboardHandlers({
    editor,
    readOnly,
    uploadSource,
    simplifyPaste = false,
}: UseClipboardHandlersConfig): UseClipboardHandlersReturn {
    // ⌘⇧V / Ctrl⇧V intent. Set on keydown, consumed by handlePaste, cleared
    // either way. A ref (not state) because the keydown→paste sequence is
    // synchronous and React state would update too late to be read by the
    // paste handler in the same tick.
    const plainTextPasteRef = useRef(false);

    const markNextPasteAsPlain = useCallback(() => {
        plainTextPasteRef.current = true;
    }, []);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            if (readOnly) return;

            const ed: any = editor;

            // ⌘⇧V intent: insert the plain-text payload, ignoring HTML AND
            // the `application/x-slate-fragment` back-channel (which Plate's
            // own paste handler would otherwise decode and reinstate every
            // mark). Splits on newlines so multi-line plain text becomes
            // multiple paragraphs, matching Google Docs / Notion behavior.
            if (plainTextPasteRef.current) {
                plainTextPasteRef.current = false;
                e.preventDefault();
                e.stopPropagation();
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (!text) return;
                const lines = text.split(/\r?\n/);
                lines.forEach((line, i) => {
                    if (i > 0) ed?.tf?.insertBreak?.();
                    if (line) ed?.tf?.insertText?.(line);
                });
                scrollCaretIntoView(ed);
                return;
            }

            // ── Slate/Plate native fragment (same or another Plate surface) ──
            // This is the only path that preserves list marks (indent +
            // listStyleType) perfectly on Plate-to-Plate copy-paste. Checked
            // BEFORE the HTML path so the rich AST wins over the lossy HTML.
            const rawFragment = e.clipboardData?.getData('application/x-slate-fragment');
            if (rawFragment) {
                try {
                    const fragment: any[] = JSON.parse(decodeURIComponent(rawFragment));
                    if (Array.isArray(fragment) && fragment.length) {
                        e.preventDefault();
                        e.stopPropagation();
                        ed?.tf?.insertFragment?.(stripNewlinesFromFragment(fragment));
                        scrollCaretIntoView(ed);
                        return;
                    }
                } catch {
                    /* malformed — fall through to HTML path */
                }
            }

            // Read the HTML up front. Excel (and Word) put a BITMAP of the
            // copied selection on the clipboard ALONGSIDE the real table HTML.
            // If the image branch below runs first it grabs that bitmap and the
            // Excel table pastes as an image — Google Sheets attaches no bitmap,
            // which is why it always worked. When the clipboard carries
            // table-shaped HTML (spreadsheet OR plain document table), prefer it
            // over the image. `htmlIsTable` gates ONLY the bitmap guard — the
            // converter-vs-generic routing below keys off `isSpreadsheetTableHtml`
            // alone.
            const html = e.clipboardData?.getData('text/html') ?? '';
            const htmlIsSpreadsheet = !!html && isSpreadsheetTableHtml(html);
            const htmlIsTable = htmlIsSpreadsheet || (!!html && isPureTableHtml(html));

            // Image file in the clipboard → image paste, UNLESS we have table
            // HTML to use instead (the Excel bitmap-vs-table case above). A
            // genuine image paste (screenshot, copied <img>) carries no
            // table-shaped HTML, so it still routes here.
            if (!htmlIsTable) {
                const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
                for (const item of items) {
                    if (item.kind === 'file' && item.type.startsWith('image/')) {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = item.getAsFile();
                        if (!file) return;
                        (async () => {
                            const result = await handleImageInsert(file, editor, uploadSource);
                            if (result.error === 'invalid-mime') toast.error('Invalid image file');
                            else if (result.error === 'too-large') toast.error('Image too large (max 8 MB)');
                            else scrollCaretIntoView(ed);
                        })();
                        return;
                    }
                }
            }

            if (!html) return;
            e.preventDefault();
            // Route to the spreadsheet converter ONLY for genuine Excel / Google
            // Sheets HTML (the `isSpreadsheetTableHtml` signatures). Detection runs
            // here, BEFORE sanitizeOfficeHtml strips the <style> blocks Excel uses
            // as its marker. `spreadsheetHtmlToPlateTable`'s cellBlocks() maps only
            // <p>/<div> → blocks and ignores <ol>/<ul>, so a DOCUMENT table (Google
            // Docs, generic HTML — isPureTableHtml true but isSpreadsheetTableHtml
            // false) must fall through to the generic deserializer, which preserves
            // cell lists. Rich documents that merely contain a table likewise fall
            // through, so the converter never collapses a multi-block paste to its
            // first table.
            if (htmlIsSpreadsheet) {
                try {
                    const tableFragment = spreadsheetHtmlToPlateTable(html);
                    if (tableFragment) {
                        ed?.tf?.insertFragment?.(stripNewlinesFromFragment([tableFragment]));
                        scrollCaretIntoView(ed);
                        return;
                    }
                } catch {
                    /* fall through */
                }
            }
            try {
                const deserialized = deserializePastedHtml(editor, html);
                // Compact inputs (comments) strip foreign styling + flatten layout
                // tables so pasted rich HTML lands as clean text (RECUR-26761).
                const fragment = deserialized && simplifyPaste ? simplifyPastedFragment(deserialized) : deserialized;
                if (fragment && fragment.length) {
                    ed?.tf?.insertFragment?.(fragment);
                    // Pasted <img> off the web (e.g. Google Docs googleusercontent
                    // URLs) lands as an image node with a remote http url that the
                    // save pipeline ignores — it would render until the source URL
                    // expires, then break. Re-host each such image into our S3 in
                    // the background (graceful no-op + warn for CORS-blocked hosts).
                    rehostRemoteImagesAfterPaste(ed, uploadSource);
                    scrollCaretIntoView(ed);
                    return;
                }
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (text) {
                    ed?.tf?.insertText?.(text);
                    scrollCaretIntoView(ed);
                }
            } catch {
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (text) {
                    ed?.tf?.insertText?.(text);
                    scrollCaretIntoView(ed);
                }
            }
        },
        // `uploadSource` belongs in deps — the original implementation in
        // PlateEditor.tsx omitted it, which would have caused stale closures
        // if the prop ever changed mid-life. Fixed during extraction.
        [editor, readOnly, uploadSource, simplifyPaste],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            if (readOnly) return;
            const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
            if (files.length === 0) return;
            // Prevent Slate's "Cannot resolve a Slate range" error on file drops.
            e.preventDefault();
            e.stopPropagation();
            const imageFiles = files.filter((f) => f.type.startsWith('image/'));
            for (const file of imageFiles) {
                (async () => {
                    const result = await handleImageInsert(file, editor, uploadSource);
                    if (result.error === 'invalid-mime') toast.error(`Invalid image file: ${file.name}`);
                    else if (result.error === 'too-large') toast.error(`Image too large (max 8 MB): ${file.name}`);
                })();
            }
        },
        // Same fix — uploadSource added to deps.
        [editor, readOnly, uploadSource],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        const types = e.dataTransfer?.types;
        if (types && Array.from(types).includes('Files')) e.preventDefault();
    }, []);

    const handleCopy = useCallback(
        (e: React.ClipboardEvent) => {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            const container =
                range.commonAncestorContainer instanceof HTMLElement
                    ? range.commonAncestorContainer
                    : range.commonAncestorContainer.parentElement;
            if (!container?.closest?.('[data-testid="plate-editor-content"]')) return;
            e.preventDefault();
            const frag = range.cloneContents();
            const wrapper = document.createElement('div');
            wrapper.appendChild(frag);
            cleanDomForExport(wrapper);

            // ── Plate AST fragment ──────────────────────────────────────────
            // Write the exact Plate/Slate AST to `application/x-slate-fragment`
            // so that pasting back into a Plate editor restores list marks
            // (indent + listStyleType) perfectly, without relying on the lossy
            // HTML round-trip. The paste handler checks for this format first.
            try {
                const plateFragment = (editor as any)?.api?.fragment?.() ?? (editor as any)?.getFragment?.();
                if (Array.isArray(plateFragment) && plateFragment.length) {
                    // getFragment drops table-level colSizes — re-attach from the
                    // source table so paste preserves column widths.
                    reattachTableColSizes(editor, plateFragment);
                    e.clipboardData.setData('application/x-slate-fragment', encodeURIComponent(JSON.stringify(plateFragment)));
                }
            } catch {
                /* defensive — fall back to HTML-only clipboard */
            }

            // ── HTML fallback (external targets: Word, Docs, plain inputs) ──
            // Enrich <li> elements with data-indent + data-list-style-type so
            // any Plate editor that receives this HTML can reconstruct list
            // marks even without the x-slate-fragment format.
            enrichPastedListElements(wrapper, 1);

            e.clipboardData.setData('text/html', `<html><body>${wrapper.innerHTML}</body></html>`);
            e.clipboardData.setData('text/plain', wrapper.textContent ?? '');
        },
        [editor],
    );

    return { handlePaste, handleDrop, handleDragOver, handleCopy, markNextPasteAsPlain };
}
